/**
 * Everything that is stopping a publish, listed in full.
 *
 * A book or capsule that is not ready used to say so one problem at a time, in a
 * transient toast naming the first block a walk happened to reach. That is the
 * wrong shape for this job. Finishing a book is *editing*, and being told about
 * one of eleven empty blocks at a time turns finishing it into a walk of its own:
 * fix, press publish, be told about the next one, repeat — with the toast's
 * message gone before the creator has scrolled to the block it named.
 *
 * So the whole list is shown at once, under the page each problem is on, and
 * every row that has somewhere to go takes the creator there: the page turns, the
 * block scrolls into view and is ringed. The rows that cannot be jumped to — a
 * problem with the book as a whole, an empty page — are drawn as what they are
 * (no chevron, not pressable) rather than as links that do nothing when pressed.
 *
 * Shared rather than copied: the FlashBook studio and the Capsule studio ask the
 * same question of the same validator, and the app's own dialog chrome answers
 * Settings → Appearance without either studio owning a colour.
 */
import {TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {atoms as a, useTheme, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {Text} from '#/components/Typography'

export type StudioIssue = {
  /** Stable identity for the row — a block id, or the book's own name. */
  key: string
  /** Where the problem is: "Cover", "Page 2", "Slide 3". */
  where: string
  /** What it is: "Table", "Title", "Empty page". */
  what: string
  /** What is missing — an instruction the creator can act on as written. */
  message: string
  /**
   * How to get there. Omitted for a problem with nowhere to point at, which is
   * drawn as a plain row instead of a link.
   */
  onOpen?: () => void
}

export function StudioIssuesDialog({
  control,
  title,
  caption,
  issues,
}: {
  control: Dialog.DialogControlProps
  /** The headline: what the publish was refused for. */
  title: string
  /** How many things are wrong, and that nothing has gone out. */
  caption: string
  issues: StudioIssue[]
}) {
  return (
    <Dialog.Outer
      control={control}
      nativeOptions={{preventExpansion: true}}
      webOptions={{alignCenter: true}}>
      <Dialog.Handle />
      <Inner title={title} caption={caption} issues={issues} />
    </Dialog.Outer>
  )
}

function Inner({
  title,
  caption,
  issues,
}: {
  title: string
  caption: string
  issues: StudioIssue[]
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const control = Dialog.useDialogContext()

  return (
    <Dialog.ScrollableInner style={web({maxWidth: 440})} label={title}>
      <View testID="studioIssuesDialog" style={[a.gap_2xl]}>
        <View style={[a.gap_sm]}>
          <Text style={[a.font_bold, a.text_2xl]}>{title}</Text>
          <Text
            style={[t.atoms.text_contrast_medium, a.text_md, a.leading_snug]}>
            {caption}
          </Text>
        </View>

        <View testID="studioIssuesList" style={[a.gap_sm]}>
          {issues.map(issue => (
            <IssueRow key={issue.key} issue={issue} />
          ))}
        </View>

        <Button
          label={l`Keep editing`}
          accessibilityHint={l`Returns to the editor with the book unchanged`}
          onPress={() => control.close()}
          size="large"
          variant="ghost"
          color="secondary"
          testID="studioIssuesClose">
          <ButtonText>{l`Keep editing`}</ButtonText>
        </Button>
      </View>
      <Dialog.Close />
    </Dialog.ScrollableInner>
  )
}

/**
 * One problem: where it is, what it is, what is missing — and, when there is
 * somewhere to go, a way to get there.
 */
function IssueRow({issue}: {issue: StudioIssue}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const control = Dialog.useDialogContext()
  const open = issue.onOpen

  const body = (
    <View style={[a.flex_row, a.align_center, a.gap_sm]}>
      <View style={[a.flex_1, a.gap_xs]}>
        <Text style={[a.text_xs, a.font_bold, t.atoms.text_contrast_medium]}>
          {issue.where} · {issue.what}
        </Text>
        <Text style={[a.text_md, a.leading_snug]}>{issue.message}</Text>
      </View>
      {open ? (
        <Feather
          name="chevron-right"
          size={16}
          color={t.atoms.text_contrast_medium.color}
        />
      ) : null}
    </View>
  )

  const row = [
    a.border,
    a.rounded_sm,
    a.px_md,
    a.py_sm,
    t.atoms.bg_contrast_25,
    t.atoms.border_contrast_low,
  ]

  if (!open) {
    // Nowhere to go: the row states the problem and stays a statement. Its
    // `testID` still names it, so a browser can read the whole list.
    return (
      <View testID={`studioIssue--${issue.key}`} style={row}>
        {body}
      </View>
    )
  }

  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityLabel={`${issue.where}, ${issue.what}: ${issue.message}`}
      accessibilityHint={l`Opens it so it can be fixed`}
      testID={`studioIssue--${issue.key}`}
      // Closed first, then the jump: turning a page or scrolling to a block
      // behind a dialog that is still on screen is a move the creator cannot
      // see happening.
      onPress={() => control.close(() => open())}
      style={row}>
      {body}
    </TouchableOpacity>
  )
}
